import { booleanOperators, numberOperators, stringOperators } from "@skilitics/judy";
import type { PrimitiveValueName } from "@skilitics/nautilus-core";

import { BooleanComparisonView, NumberComparisonView, StringComparisonView } from "../forms/comparison-views/index.ts";
import type { ValueSubtypeDefinition } from "../valueSubtypeDefinition.ts";
import type { ValueSubtype } from "../valueSubtypes.ts";
import type { NumberValueSubtypeSpec } from "../valueSubtypeSpecs.ts";
import { defaultNumberLiteral, displayNumberLiteral } from "./numberSpec.ts";

/**
 * The definition for a plain value of its base type — no `id`, because it names
 * no narrowing. Core ships one per primitive type, and they are the fallback
 * whenever a subject declares no subtype or one nobody contributed a definition
 * for, so an expression over an unfamiliar value stays editable rather than
 * failing to render.
 *
 * Having no `require` is the point: an unrefined slot accepts any value of its
 * base type, which is exactly what an absent requirement means to
 * `subtypeSatisfies`.
 */
const numberDefinition: ValueSubtypeDefinition<ValueSubtype, NumberValueSubtypeSpec> = {
  id: undefined,
  type: "number",
  operators: numberOperators,
  // the spec's bounds are what makes a plain number's validity more than "is it a
  // number"; `isValid` judges a literal, so this is where they bear
  isValid: (value, _subtype, spec) =>
    Number.isFinite(value) &&
    (spec?.min === undefined || (value as number) >= spec.min) &&
    (spec?.max === undefined || (value as number) <= spec.max),
  defaultValue: (_subtype, spec) => defaultNumberLiteral(spec),
  formatLiteral: (value, _subtype, spec) =>
    typeof value === "number" ? displayNumberLiteral(value, spec) : String(value),
  views: { comparison: NumberComparisonView },
};

const stringDefinition: ValueSubtypeDefinition = {
  id: undefined,
  type: "string",
  operators: stringOperators,
  isValid: (value) => typeof value === "string",
  views: { comparison: StringComparisonView },
};

const booleanDefinition: ValueSubtypeDefinition = {
  id: undefined,
  type: "boolean",
  operators: booleanOperators,
  isValid: (value) => typeof value === "boolean",
  views: { comparison: BooleanComparisonView },
};

const baseDefinitions: Record<PrimitiveValueName, ValueSubtypeDefinition> = {
  // a definition narrows its spec type where it reads one; the resolver hands out
  // definitions over any base type, so the narrowing is widened back here
  number: numberDefinition as ValueSubtypeDefinition,
  string: stringDefinition,
  boolean: booleanDefinition,
};

/** The definition for a base type, with no narrowing in play. */
export const baseValueSubtypeDefinition = (type: PrimitiveValueName): ValueSubtypeDefinition => baseDefinitions[type];
